import { Box, BoxProps, Text, useDisclosure } from "@chakra-ui/react";
import { format, parseISO } from "date-fns";
import {
  CircularLoader,
  ExchangeInfoAccordion,
  TransactionDetailItem,
  UploadProofModal,
  WarningText,
  WidgetStatusStepper,
} from "components";
import { toPrecision, showFirstLastChar, currencyFormat, toCoinLocale } from "utils";
import { HistoryDetails } from "interfaces";
import { useMemo } from "react";

interface ITransferData {
  isSuccess: boolean;
  data: HistoryDetails;
}
const TransferData = ({ isSuccess, data }: ITransferData) => {
  const statusBg = useMemo(() => {
    if (
      [
        "TransferCancelled",
        "TransferComplete",
        "TransferFailed",
        "TransferAmlFailed",
        "PaymentCancelled",
        "PaymentIncomplete",
        "PaymentFailed",
      ].includes(data?.paymentStatus)
    ) {
      return "initial";
    }
    if (
      [
        "TransferCancelled",
        "TransferComplete",
        "TransferFailed",
        "TransferAmlFailed",
        "PaymentCancelled",
        "PaymentIncomplete",
        "PaymentFailed",
      ].includes(data?.status)
    ) {
      return "initial";
    }
  }, [data]);

  const showWarningText = useMemo(() => {
    if (!["TransferComplete", "TransferCancelled", "TransferFailed"].includes(data?.status)) {
      return true;
    }
    if (["PaymentFailed", "PaymentCancelled"].includes(data?.paymentStatus)) {
      return false;
    }
  }, [data]);

  const amountTitle = useMemo(() => {
    const netCryptoAmount = toPrecision(data?.netCryptoAmount!, data?.cryptoType?.toLowerCase());
    const cryptoAmount = toPrecision(data?.cryptoAmount!, data?.cryptoType?.toLowerCase());
    const amountProvided = currencyFormat((data?.fiatType?.toLowerCase() as any) ?? "ngn" ?? data?.fiatType).format(
      +(data?.amountProvided ?? 0)
    );

    const amountReceived = currencyFormat((data?.fiatType?.toLowerCase() as any) ?? "ngn").format(+(data?.amountReceived ?? 0));
    return data?.direction === "in" ? `${netCryptoAmount} for ${amountProvided}` : `${cryptoAmount} for ${amountReceived}`;
  }, [data]);

  const fiatAmount = useMemo(() => {
    const result = `${Number(
      data?.direction === "in" ? data?.amountProvided : data?.amountReceived
    ).toLocaleString()} ${data?.fiatType?.toUpperCase()}`;

    return result;
  }, [data]);

  const { isOpen, onClose, onOpen } = useDisclosure();

  return (
    <Box pt={{ base: "8px", md: "16px" }} mt={{ base: "10px", md: "20px" }}>
      {!isSuccess && <CircularLoader />}

      {isSuccess && (
        <>
          <Box>
            <Text fontWeight="600" mb="4px" fontSize="14px">
              Transfer Id
            </Text>

            <Box>
              <TransactionDetailItem
                p="0 24px 0 0"
                title=""
                description={data?.transferId}
                _description={{ color: "#0D4740", fontSize: "16px", fontWeight: "500" }}
                isCopyable
              />
            </Box>
          </Box>

          <Box margin={{ base: "12px 0", md: "24px 0" }}>
            <Text fontWeight="600" mb="4px" fontSize="14px">
              Status
            </Text>

            <Box
              bg={statusBg ?? "#F8F9F9"}
              borderRadius="10px"
              padding={{ base: statusBg ? "10px 0" : "10px 12px", "1sm": statusBg ? "15.5px 0" : '"15.5px 12px"' }}
            >
              <WidgetStatusStepper
                coin={data.cryptoType}
                type={data?.transferType!}
                paymentStatus={data?.paymentStatus!}
                transferStatus={data?.status!}
                reason={data?.reason}
              />
            </Box>
          </Box>

          {showWarningText && (
            <WarningText>
              <Text>
                {data?.direction === "in" ? (
                  <>
                    We're in the process of verifying your payment and transferring{" "}
                    <Text as="span" fontWeight="500">
                      {toCoinLocale(data?.cryptoType.toLowerCase())}
                    </Text>{" "}
                    to your wallet.
                  </>
                ) : (
                  <>
                    We're in the process of verifying your{" "}
                    <Text as="span" fontWeight="500">
                      {toCoinLocale(data?.cryptoType.toLowerCase())}
                    </Text>{" "}
                    transfer and will proceed with transferring the fiat equivalent to your account shortly.
                  </>
                )}{" "}
                Anticipated completion time is{" "}
                <Text as="span" fontWeight="600">
                  2 - 5 minutes
                </Text>
                . Feel free to close this page; we'll notify you via email about the transaction status.
              </Text>
            </WarningText>
          )}

          <Box>
            <Text fontWeight="600" mb="4px" fontSize="14px">
              Summary
            </Text>

            <ExchangeInfoAccordion
              title={`You ${data.direction === "in" ? "bought" : "sold"}
                ${amountTitle} `}
              isOpen={false}
              closeWindow
            >
              <Box>
                <BoxCard title="Fiat Amount" description={fiatAmount} />
                <BoxCard title="Crypto Volume" description={`${toPrecision(data?.cryptoAmount!, data.cryptoType.toLowerCase())}`} />

                {data?.totalCryptoFee >= 0 && (
                  <BoxCard
                    title="Total Crypto Fee"
                    description={`${toPrecision(data?.totalCryptoFee!, data.cryptoType.toLowerCase())}`}
                  />
                )}

                {data?.cryptoTypeNetwork && (
                  <BoxCard title="Crypto Network" description={data.cryptoTypeNetwork.replace("_", " ").toUpperCase()} />
                )}

                {data.direction === "in" && (
                  <TransactionDetailItem
                    mb="16px"
                    p="0"
                    title="Payment URL"
                    cursor="pointer"
                    description={
                      <Box color="blue.500" textDecor="underline" _hover={{ fontWeight: "500", transition: "all 0.5s ease-in" }}>
                        {data.paymentUrl ? (
                          <Box as="a" href={data.paymentUrl} target="_blank" rel="noopener noreferrer">
                            View proof
                          </Box>
                        ) : (
                          <Box onClick={onOpen}>Upload proof</Box>
                        )}
                      </Box>
                    }
                  />
                )}

                {data?.userAddress && (
                  <TransactionDetailItem
                    mb="16px"
                    p="0"
                    title="Address"
                    moreDescription={showFirstLastChar(data.userAddress, 6)}
                    description={data.userAddress}
                    isCopyable
                  />
                )}

                {data?.transferAddress && (
                  <TransactionDetailItem
                    mb="16px"
                    p="0"
                    title="Transfer Address"
                    moreDescription={showFirstLastChar(data.transferAddress, 6)}
                    description={data.userAddress}
                    isCopyable
                  />
                )}

                <TransactionDetailItem mb="16px" p="0" title="Transaction Ref" description={data?.reference} isCopyable />

                {data?.userMemo && <TransactionDetailItem mb="16px" p="0" title="Memo" description={data?.userMemo} isCopyable />}

                {data?.userTagNumber && (
                  <TransactionDetailItem mb="16px" p="0" title="Tag Number" description={data?.userTagNumber} isCopyable />
                )}

                {data?.txHash && (
                  <TransactionDetailItem
                    mb="16px"
                    p="0"
                    title="Transaction Hash"
                    moreDescription={showFirstLastChar(data.txHash, 6)}
                    description={data?.txHash}
                    isCopyable
                  />
                )}

                {data?.txLink && (
                  <BoxCard title="Transaction Link" description="">
                    <Box as="a" textDecor="underline" href={data?.txLink} target="_blank" rel="noopener noreferrer">
                      {showFirstLastChar(data?.txLink!, 10)}
                    </Box>
                  </BoxCard>
                )}

                {data?.createdAt && (
                  <BoxCard title="Date & Time" description="" mt="14px !important">
                    {format(parseISO(data.createdAt), "dd MMM, yyyy")} &nbsp; {format(parseISO(data.createdAt), "HH:mm a")}
                  </BoxCard>
                )}
              </Box>
            </ExchangeInfoAccordion>
          </Box>

          {isOpen && <UploadProofModal transferId={data.transferId} isOpen={isOpen} onClose={onClose} hideSkip />}
        </>
      )}
    </Box>
  );
};

export default TransferData;

interface IProps extends BoxProps {
  title: string;
  description: string;
  children?: React.ReactNode;
  descColor?: string;
}
const BoxCard = ({ description, title, children, descColor, ...props }: IProps) => {
  return (
    <Box mb="16px" {...props}>
      <Text fontWeight="600" mb="4px" fontSize="14px">
        {title}
      </Text>

      <Box as="div" fontWeight="500" color={descColor ? descColor : "#797A7A"} fontSize="14px">
        {description} {children}
      </Box>
    </Box>
  );
};
